import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { Bank, BankService } from '../../services/bank.service';
import { closestTextMatch } from '../../utils/closest-match';

@Component({
  selector: 'app-bank-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="bank-select" *ngIf="!showCustom()">
      <div class="bank-control" [class.invalid]="invalid">
        <i class="bi bi-bank2"></i>
        <input
          #searchInput
          type="text"
          [placeholder]="placeholder"
          [(ngModel)]="searchText"
          (focus)="open()"
          (blur)="onBlur()"
          (input)="onType()"
          (keydown)="onKeydown($event)"
          autocomplete="off"
        >
        <i class="bi bi-chevron-down bank-caret" [class.up]="isOpen()"></i>
      </div>
      <div class="bank-panel" *ngIf="isOpen()">
        <button
          type="button"
          *ngFor="let bank of filtered(); let i = index; trackBy: trackBank"
          class="bank-option"
          [class.active]="i === activeIndex()"
          (mousedown)="$event.preventDefault(); pick(bank)"
        >{{ bank.name }}</button>
        <p class="bank-empty" *ngIf="!filtered().length">Sin coincidencias.</p>
        <button
          type="button"
          class="bank-option bank-other"
          [class.active]="activeIndex() === filtered().length"
          (mousedown)="$event.preventDefault(); pickOther()"
        ><i class="bi bi-three-dots"></i> Otro</button>
      </div>
    </div>
    <div class="bank-custom" *ngIf="showCustom()">
      <div class="bank-control" [class.invalid]="invalid">
        <i class="bi bi-pencil"></i>
        <input
          #customInput
          type="text"
          placeholder="Nombre del banco"
          [(ngModel)]="customName"
          (input)="customNameChange.emit(customName); nameChange.emit(customName)"
          maxlength="120"
        >
      </div>
      <button type="button" class="bank-back" (click)="backToCatalog()"><i class="bi bi-arrow-left"></i> Elegir del catálogo</button>
    </div>
  `,
  styles: [`
:host{position:relative;display:block;width:100%}
.bank-control{position:relative;display:flex;align-items:center;gap:.5rem;box-sizing:border-box;width:100%;min-height:44px;padding:0 .7rem;border:1px solid var(--ts-border-strong);border-radius:9px;background:var(--ts-input-bg, rgba(4,18,36,.72));transition:border-color .15s ease,box-shadow .15s ease}
.bank-control:focus-within{border-color:var(--ts-primary);box-shadow:0 0 0 3px var(--ts-primary-soft-14)}
.bank-control.invalid{border-color:var(--ts-danger)}
.bank-control>i:first-child{color:var(--ts-text-muted);font-size:.9rem}
.bank-control input,.bank-control input:hover,.bank-control input:focus{appearance:none!important;-webkit-appearance:none!important;flex:1;min-width:0;height:42px!important;min-height:0!important;margin:0!important;padding:0!important;border:0!important;outline:0!important;box-shadow:none!important;border-radius:0!important;background:transparent!important;color:var(--ts-text)!important;font-size:.85rem;font-family:inherit}
.bank-caret{color:var(--ts-text-muted);font-size:.75rem;transition:transform .15s ease}
.bank-caret.up{transform:rotate(180deg)}
.bank-panel{position:absolute;z-index:30;top:calc(100% + 6px);left:0;right:0;max-height:260px;overflow-y:auto;box-sizing:border-box;padding:.35rem;border:1px solid var(--ts-border-strong);border-radius:11px;background:var(--ts-surface-2, #0f2743);box-shadow:var(--ts-shadow-lg, 0 24px 60px rgba(0,0,0,.4))}
.bank-option{display:block;width:100%;box-sizing:border-box;padding:.55rem .65rem;border:0;border-radius:7px;background:transparent;color:var(--ts-text);font-size:.82rem;text-align:left;cursor:pointer}
.bank-option:hover,.bank-option.active{background:var(--ts-primary-soft-11)}
.bank-other{display:flex;align-items:center;gap:.4rem;margin-top:.2rem;padding-top:.55rem;border-top:1px solid var(--ts-border);color:var(--ts-text-muted)}
.bank-empty{margin:0;padding:.5rem .65rem;color:var(--ts-text-muted);font-size:.78rem}
.bank-custom{display:grid;gap:.4rem}
.bank-back{display:inline-flex;align-items:center;gap:.3rem;width:fit-content;padding:0;border:0;background:transparent;color:var(--ts-primary);font-size:.72rem;font-weight:600;cursor:pointer}
.bank-back:hover{text-decoration:underline}
  `],
})
export class BankSelectComponent implements OnInit {
  @Input() bankId: number | null = null;
  @Input() customName = '';
  @Input() invalid = false;
  @Input() placeholder = 'Buscar banco…';
  @Output() bankIdChange = new EventEmitter<number | null>();
  @Output() customNameChange = new EventEmitter<string>();
  /** Nombre a mostrar/guardar: el del catálogo al elegir un banco, o el escrito a mano en "Otro". */
  @Output() nameChange = new EventEmitter<string>();

  private readonly bankService = inject(BankService);

  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('customInput') customInputRef?: ElementRef<HTMLInputElement>;

  private banks = signal<Bank[]>([]);
  isOpen = signal(false);
  activeIndex = signal(-1);
  searchText = '';
  showCustom = signal(false);

  ngOnInit(): void {
    this.showCustom.set(this.bankId === null && !!this.customName);
    this.bankService.list().subscribe({
      next: (res) => {
        this.banks.set(res.banks || []);
        const match = this.bankId ? this.banks().find((b) => b.id === this.bankId) : null;
        if (match) this.searchText = match.name;
      },
      error: () => this.banks.set([]),
    });
  }

  filtered(): Bank[] {
    const term = this.searchText.trim().toLowerCase();
    if (!term) return this.banks();
    return this.banks().filter((b) => b.name.toLowerCase().includes(term));
  }

  open(): void {
    this.isOpen.set(true);
    this.activeIndex.set(-1);
  }

  onType(): void {
    this.isOpen.set(true);
    this.activeIndex.set(-1);
    if (this.bankId !== null) {
      this.bankId = null;
      this.bankIdChange.emit(null);
    }
    // Escribir texto libre en el buscador no cuenta como selección: hasta que
    // no se elija una opción real de la lista (o "Otro"), no se guarda ningún
    // nombre — evita que quede un institution_name obsoleto o inventado.
    this.nameChange.emit('');
  }

  onBlur(): void {
    this.resolveTypedSelection();
  }

  /** Si el campo pierde el foco (click fuera) o se presiona Enter sin haber
   * navegado a una opción con las flechas, se autoselecciona el banco más
   * cercano a lo escrito (p. ej. "agr" -> "Agrícola"), igual que si el
   * usuario lo hubiera elegido de la lista. Solo se limpia el texto si no
   * hay ninguna coincidencia posible. */
  private resolveTypedSelection(): void {
    this.isOpen.set(false);
    if (this.bankId === null && this.searchText.trim()) {
      const match = this.closestMatch(this.searchText);
      if (match) this.pick(match);
      else this.searchText = '';
    }
  }

  private closestMatch(term: string): Bank | null {
    return closestTextMatch(term, this.banks(), (b) => b.name);
  }

  /** Hay una selección válida solo si se eligió un banco del catálogo, o se
   * está en modo "Otro" con un nombre escrito. Texto suelto sin confirmar no cuenta. */
  hasValidSelection(): boolean {
    return this.bankId !== null || (this.showCustom() && this.customName.trim().length > 0);
  }

  focusSearch(): void {
    const target = this.showCustom() ? this.customInputRef?.nativeElement : this.searchInputRef?.nativeElement;
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('field-error-flash');
    target.focus();
    setTimeout(() => target.classList.remove('field-error-flash'), 1900);
  }

  pick(bank: Bank): void {
    this.bankId = bank.id;
    this.searchText = bank.name;
    this.customName = '';
    this.bankIdChange.emit(bank.id);
    this.customNameChange.emit('');
    this.nameChange.emit(bank.name);
    this.isOpen.set(false);
  }

  pickOther(): void {
    this.bankId = null;
    this.searchText = '';
    this.bankIdChange.emit(null);
    this.isOpen.set(false);
    this.showCustom.set(true);
  }

  backToCatalog(): void {
    this.showCustom.set(false);
    this.customName = '';
    this.customNameChange.emit('');
    this.nameChange.emit('');
  }

  trackBank(_: number, bank: Bank): number {
    return bank.id;
  }

  onKeydown(evt: KeyboardEvent): void {
    const options = this.filtered();
    const maxIndex = options.length; // "Otro" siempre es la última opción
    if (evt.key === 'ArrowDown') {
      evt.preventDefault();
      this.isOpen.set(true);
      this.activeIndex.set(Math.min(this.activeIndex() + 1, maxIndex));
    } else if (evt.key === 'ArrowUp') {
      evt.preventDefault();
      this.activeIndex.set(Math.max(this.activeIndex() - 1, 0));
    } else if (evt.key === 'Enter') {
      evt.preventDefault();
      const index = this.activeIndex();
      if (index === maxIndex) this.pickOther();
      else if (index >= 0 && options[index]) this.pick(options[index]);
      else this.resolveTypedSelection();
    } else if (evt.key === 'Escape') {
      this.isOpen.set(false);
    }
  }

}
